import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import DraggableList from "react-draggable-list";
import { useMenuState } from "@szhsin/react-menu";

import ContextMenu from "components/context-menu/context-menu.component";
import { ReactComponent as Handle } from "assets/drag-handle.svg";

import { FolderContext } from "src/contexts/folder.context";

import "./table.style.scss";
import useTranslate from "src/hooks/use-translate";

const COLUMNS = [
	{ field: "ref", name: "reference", flex: 3 },
	{ field: "fileName", name: "name", flex: 10 },
	{ field: "created", name: "created", flex: 6 },
];

const HoverMenu = ({ className }) => {
	return (
		<div
			className={`${className} flex flex--column flex--center-content size300 padding-s--inline smooth-corners`}
		>
			<p className="line">
				<span className="first-word">Drag</span> to move
			</p>
			<p className="line">
				<span className="first-word">Click</span> to open menu
			</p>
		</div>
	);
};

const Item = ({ item, itemSelected, dragHandleProps }) => {
	const { openFile, deleteFile, setFileToBeMoved, getUpdatedFile } = useContext(FolderContext);
	const [menuProps, toggleMenu] = useMenuState();
	const { onMouseDown, onTouchStart } = dragHandleProps;
	const [anchorPoint, setAnchorPoint] = useState({ x: 0, y: 0 });
	const timeoutRef = useRef();

	const t = useTranslate("Archive");

	const { ref, file, createdAt } = item;
	const fileName = useMemo(() => file.name, [file]);

	const handleOpenFile = useCallback(() => {
		openFile(fileName);
	}, [fileName, openFile]);

	const handleOpenOriginalFile = useCallback(() => {
		openFile(fileName, true);
	}, [fileName, openFile]);

	const handleDownloadFile = useCallback(async () => {
		const fileURL = window.URL.createObjectURL(await getUpdatedFile(file, ref));
		// Setting various property values
		let alink = document.createElement("a");
		alink.href = fileURL;
		alink.download = fileName;
		alink.click();
	}, [file, fileName, ref, getUpdatedFile]);

	const handleDownloadOriginalFile = useCallback(() => {
		const fileURL = window.URL.createObjectURL(file);
		// Setting various property values
		let alink = document.createElement("a");
		alink.href = fileURL;
		alink.download = fileName;
		alink.click();
	}, [file, fileName]);

	const handleMoveFile = useCallback(() => {
		setFileToBeMoved(fileName);
	}, [fileName, setFileToBeMoved]);

	const handleDeleteFile = useCallback(() => {
		deleteFile(fileName);
	}, [fileName, deleteFile]);

	const handleContextMenu = useCallback(
		(e) => {
			e.preventDefault();
			setAnchorPoint({ x: e.clientX, y: e.clientY });
			toggleMenu(true);
		},
		[toggleMenu]
	);

	const contextMenuList = useMemo(
		() => [
			{
				label: t("view file"),
				onClick: handleOpenFile,
			},
			{
				label: t("view original file"),
				onClick: handleOpenOriginalFile,
			},
			{
				label: t("download file"),
				onClick: handleDownloadFile,
			},
			{
				label: t("download original file"),
				onClick: handleDownloadOriginalFile,
			},
			{
				label: t("move file"),
				onClick: handleMoveFile,
			},
			{
				label: t("delete file"),
				onClick: handleDeleteFile,
			},
		],
		[
			t,
			handleOpenFile,
			handleOpenOriginalFile,
			handleDownloadFile,
			handleDownloadOriginalFile,
			handleMoveFile,
			handleDeleteFile,
		]
	);

	return (
		<Fragment>
			<div className={`body-item grid pointer padding-m--block ${itemSelected && "selected-row"}`}>
				<span
					className="drag-handler pointer text-center"
					onTouchStart={(e) => {
						e.preventDefault();
						onTouchStart(e);
					}}
					onMouseDown={(e) => {
						if (e.button !== 0) return; // if not left click
						timeoutRef.current = setTimeout(() => {
							onMouseDown(e);
						}, 300);
					}}
					onMouseUp={(e) => {
						clearTimeout(timeoutRef.current);
					}}
					onMouseLeave={(e) => {}}
					onClick={(e) => {
						if (itemSelected) return;
						handleContextMenu(e);
					}}
					onContextMenu={handleContextMenu}
				>
					<Handle className="handle" style={{ height: "1.2rem" }} />
					<HoverMenu className="hover-menu" />
				</span>
				<span onClick={handleOpenFile}>{ref.length ? ref : "N/A"}</span>
				<span onClick={handleOpenFile}>{fileName}</span>
				<span onClick={handleOpenFile}>{createdAt}</span>
				<span onClick={handleDeleteFile}>X</span>
			</div>
			<ContextMenu
				list={contextMenuList}
				menuProps={menuProps}
				toggleMenu={toggleMenu}
				anchorPoint={anchorPoint}
			/>
		</Fragment>
	);
};

const Table = () => {
	const { focusedFiles, addFile, sortFiles } = useContext(FolderContext);
	const [rows, setRows] = useState([]);

	const t = useTranslate("Archive");

	const inputRef = useRef();
	const containerRef = useRef();

	// const myTable = useReactTable({
	// 	// data: list,
	// 	data: rows,
	// 	columns: columns,
	// 	getCoreRowModel: getCoreRowModel(),
	// });

	useEffect(() => {
		setRows(() => {
			return focusedFiles.files;
			// return focusedFiles.files.map(({ ref, createdAt, file }) => {
			// 	return { ref: ref, fileName: file.name, created: createdAt, file: file };
			// });
		});
	}, [focusedFiles]);

	const handleListChange = (newList) => {
		sortFiles(newList);
	};

	const handleClick = () => {
		if (!inputRef) return;

		inputRef.current.click();
	};

	const handleNewFile = (event) => {
		if (event.target.files === "") return;

		for (var i = 0; i < event.target.files.length; i++) {
			const file = event.target.files[i];
			addFile(file);
		}
	};

	return (
		<div className="table-container flex flex--column">
			<input
				type="file"
				style={{ display: "none" }}
				ref={inputRef}
				onChange={handleNewFile}
				multiple
			/>
			{rows.length > 0 && (
				<Fragment>
					<div className="header grid flex--center-items padding-s--block">
						<span></span>
						{COLUMNS.map(({ name }, index) => (
							<span key={index} className="w600">
								{t(name)}
							</span>
						))}
						<span></span>
					</div>

					<div ref={containerRef} className="body scroll-bar">
						<DraggableList
							itemKey={(item) => item.file.name}
							template={Item}
							list={rows}
							onMoveEnd={handleListChange}
							padding={0}
							constrainDrag={true}
							container={() => containerRef.current}
							unsetZIndex={true}
						/>
					</div>
				</Fragment>
			)}
			<div className="footer grid unselectable padding-s--block">
				<span></span>
				<span onClick={handleClick} className="pointer">
					+ {t("new")}
				</span>
				<span>
					{t("count")}: {rows.length}
				</span>
				<span></span>
			</div>
		</div>
	);

	// return (
	// 	<div className="table-container">
	// 		<input type="file" style={{ display: "none" }} ref={inputRef} onChange={handleNewFile} />
	// 		<div className="table">
	// 			<table>
	// 				{rows.length > 0 && (
	// 					<thead>
	// 						<tr className="table-header">
	// 							{COLUMNS.map(({ name, flex }, index) => (
	// 								<th key={index} className="header-text" style={{ flex: flex }}>
	// 									{name}
	// 								</th>
	// 							))}
	// 						</tr>
	// 					</thead>
	// 				)}
	// 				<tbody>
	// 					{rows.map(({ ref, fileName, created, file }, index) => {
	// 						return (
	// 							<tr
	// 								key={index}
	// 								onClick={() => {
	// 									handleOpenFile(fileName);
	// 								}}
	// 							>
	// 								<td>{ref}</td>
	// 								<td>{fileName}</td>
	// 								<td>{created}</td>
	// 							</tr>
	// 						);
	// 					})}
	// 				</tbody>
	// 				<tfoot>
	// 					<tr className="table-footer">
	// 						<td onClick={handleClick}>+ New</td>
	// 						<td>Count: {rows.length}</td>
	// 						<td></td>
	// 					</tr>
	// 				</tfoot>
	// 			</table>
	// 			{/* <div onClick={handleClick} className="new-file-button">
	// 				<div className="text">{`+ New		Count: ${rows.length}`}</div>
	// 			</div> */}
	// 		</div>
	// 	</div>
	// );
};

export default Table;
